<template>
  <div>
    <v-toolbar
      dense
      tabs
      color="primary darken-2"
      dark
      class="page_title"
    >
      <v-toolbar-title class="pa-0 pr-0 mt-1">
        <v-layout row>
          <v-flex xs10 sm11 text-xs-left>
            <a href="javascript: history.go(-1)" class="pa-0">
              <v-btn flat fab small class="pa-0">
                <i class="fas fa-arrow-left headline"></i>
              </v-btn>
            </a>
           Create Investment
          </v-flex>
          <v-flex xs2 sm1 text-xs-right>
           <router-link :to="'/savings'" class="pa-0 text-xs-right">
              <v-btn flat fab small class="pa-0">
                <i class="fas fa-close headline"></i>
              </v-btn>
            </router-link>
          </v-flex>
        </v-layout>
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-tabs
        v-model="tabs"
        color="transparent"
        slider-color="white"
        slot="extension"
        grow
      >
        <v-tab
          v-for="tabsItem in tabsItems"
          :key="tabsItem.id"
          :to="tabsItem.link"
        >
          {{ tabsItem.title }}
        </v-tab>
      </v-tabs>
    </v-toolbar>

    <div class="investment-container" ref="container">
        <v-card class="elevation-8 investment-main-card">
            <div class="investment-header">
                <div class="header-icon-wrapper">
                    <v-icon large color="white" class="header-icon">fas fa-chart-line</v-icon>
                </div>
                <h2 class="white--text font-weight-bold text-xs-center header-title">
                    Create an Investment
                </h2>
            </div>

            <v-card-text class="pa-3 pa-sm-5">
                <v-alert type="info" border="left" colored-border elevation="2" class="mb-4 terms-alert">
                    <div class="subtitle-1 font-weight-bold mb-2">
                        <v-icon color="info">fas fa-info-circle</v-icon>
                        Investment Terms & Conditions
                    </div>
                    <ul class="pl-3 terms-list">
                        <li>Duration: Exactly 12 months (1 year)</li>
                        <li>Monthly Payments: 10 installments over 12 months</li>
                        <li><strong>Monthly Interest:</strong> Decreasing rates (18%, 16.5%, 15%, 13.5%, 12%, 10.5%, 9%, 7.5%, 6%, 4.5%)</li>
                        <li><strong>One-Time Interest:</strong> Fixed 18% at the end</li>
                        <li>Grace Period: 2 days after each due date</li>
                        <li>Late Penalty: 18% of monthly payment if late (after 1st month)</li>
                        <li>First Payment: Must be made within 2 days or cancellation</li>
                        <li><strong>Important:</strong> First month must be paid in full. Months 2-10 allow partial payments.</li>
                    </ul>
                </v-alert>

                <div class="mb-5">
                    <h3 class="teal--text text--darken-2 mb-3 section-title">
                        <v-icon color="teal darken-2" class="mr-2">fas fa-hand-holding-usd</v-icon>
                        Select Investment Amount
                    </h3>

                    <v-layout row wrap>
                        <v-flex
                            xs6 sm6 md4
                            v-for="amount in availableAmounts"
                            :key="amount.value"
                            class="pa-1 pa-sm-2"
                        >
                            <v-card
                                :class="['amount-card', { 'selected': selectedAmount === amount.value }]"
                                @click="selectAmount(amount.value)"
                                hover
                                :elevation="selectedAmount === amount.value ? 12 : 2"
                            >
                                <v-card-text class="text-xs-center pa-2 pa-sm-3">
                                    <div class="amount-value">{{ formatAmount(amount.value) }}</div>
                                    <div class="amount-label">FCFA</div>
                                    <v-divider class="my-1 my-sm-2"></v-divider>
                                    <div class="interest-info">
                                        <small>Monthly: <strong class="purple--text">{{ formatAmount(amount.value / 10) }} FCFA</strong></small>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-flex>
                    </v-layout>
                </div>

                <div class="mb-5" v-if="selectedAmount" ref="paymentTypeSection">
                    <h3 class="teal--text text--darken-2 mb-3 section-title">
                        <v-icon color="teal darken-2" class="mr-2">fas fa-credit-card</v-icon>
                        Choose Your Payment Method
                    </h3>

                    <v-layout row wrap>
                        <v-flex xs12 md6 class="pa-1 pa-sm-2">
                            <v-card
                                :class="['payment-type-card', { 'selected': paymentType === 'monthly' }]"
                                @click="selectPaymentType('monthly')"
                                hover
                                :elevation="paymentType === 'monthly' ? 12 : 2"
                            >
                                <v-card-text class="pa-2 pa-sm-3">
                                    <div class="text-xs-center mb-2 mb-sm-3">
                                        <v-icon :size="$vuetify.breakpoint.xs ? 36 : 48" color="purple darken-2">fas fa-calendar-alt</v-icon>
                                    </div>
                                    <h4 class="text-xs-center mb-2 purple--text text--darken-2 payment-title">Monthly Payment</h4>
                                    <v-divider class="my-1 my-sm-2"></v-divider>
                                    <div class="payment-details">
                                        <p class="mb-1"><strong>Monthly Amount:</strong></p>
                                        <p class="display-1 teal--text text--darken-2 mb-2 payment-amount">{{ formatAmount(selectedAmount / 10) }} FCFA</p>
                                        <p class="caption mb-1">✓ 10 monthly payments over 12 months</p>
                                        <p class="caption mb-1">✓ <strong>Decreasing interest</strong>: 18% → 4.5%</p>
                                        <p class="caption mb-1">✓ Total interest: <strong class="green--text">{{ formatAmount(calculateMonthlyTotalInterest()) }} FCFA</strong></p>
                                        <p class="caption mb-1">✓ First month: full payment required</p>
                                        <p class="caption">✓ Months 2-10: partial payments allowed</p>

                                        <v-expansion-panel class="mt-2 mt-sm-3">
                                            <v-expansion-panel-content>
                                                <template v-slot:header>
                                                    <div class="caption teal--text">
                                                        <v-icon small color="teal">fas fa-info-circle</v-icon>
                                                        View interest breakdown
                                                    </div>
                                                </template>
                                                <v-card flat>
                                                    <v-card-text class="pa-1 pa-sm-2">
                                                        <table class="interest-table">
                                                            <tr v-for="month in 10" :key="month">
                                                                <td class="caption">Month {{ month }}:</td>
                                                                <td class="caption font-weight-bold">{{ getMonthlyInterestRate(month) }}%</td>
                                                                <td class="caption green--text">+{{ formatAmount(getMonthlyInterestAmount(month)) }} FCFA</td>
                                                            </tr>
                                                        </table>
                                                    </v-card-text>
                                                </v-card>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-flex>

                        <v-flex xs12 md6 class="pa-1 pa-sm-2">
                            <v-card
                                :class="['payment-type-card', { 'selected': paymentType === 'one_time' }]"
                                @click="selectPaymentType('one_time')"
                                hover
                                :elevation="paymentType === 'one_time' ? 12 : 2"
                            >
                                <v-card-text class="pa-2 pa-sm-3">
                                    <div class="text-xs-center mb-2 mb-sm-3">
                                        <v-icon :size="$vuetify.breakpoint.xs ? 36 : 48" color="green darken-2">fas fa-hand-holding-usd</v-icon>
                                    </div>
                                    <h4 class="text-xs-center mb-2 green--text text--darken-2 payment-title">One-Time Payment</h4>
                                    <v-divider class="my-1 my-sm-2"></v-divider>
                                    <div class="payment-details">
                                        <p class="mb-1"><strong>Single Amount:</strong></p>
                                        <p class="display-1 teal--text text--darken-2 mb-2 payment-amount">{{ formatAmount(selectedAmount) }} FCFA</p>
                                        <p class="caption mb-1">✓ Only one payment</p>
                                        <p class="caption mb-1">✓ No penalty risk</p>
                                        <p class="caption mb-1">✓ <strong>Fixed interest</strong>: 18%</p>
                                        <p class="caption">✓ Interest paid at end: <strong class="green--text">{{ formatAmount(selectedAmount * 0.18) }} FCFA</strong></p>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-flex>
                    </v-layout>
                </div>

                <v-card v-if="selectedAmount && paymentType" class="summary-card" color="grey lighten-4" elevation="3" ref="summarySection">
                    <div class="summary-header">
                        <div class="summary-header-icon">
                            <v-icon color="white" size="28">fas fa-file-invoice-dollar</v-icon>
                        </div>
                        <div class="summary-header-text">
                            <h3 class="white--text font-weight-bold mb-0">Investment Summary</h3>
                            <span class="white--text caption summary-header-sub">Review & Confirm Your Details</span>
                        </div>
                    </div>

                    <div class="summary-financial pa-3 pa-sm-4">
                        <div class="financial-grid">
                            <div class="financial-item">
                                <div class="financial-icon-wrapper teal lighten-4">
                                    <v-icon color="teal darken-2" size="20">fas fa-coins</v-icon>
                                </div>
                                <div class="financial-content">
                                    <span class="financial-label">Investment Amount</span>
                                    <span class="financial-value">{{ formatAmount(selectedAmount) }} FCFA</span>
                                </div>
                            </div>

                            <div class="financial-item">
                                <div class="financial-icon-wrapper purple lighten-4">
                                    <v-icon color="purple darken-2" size="20">fas fa-wallet</v-icon>
                                </div>
                                <div class="financial-content">
                                    <span class="financial-label">Payment Type</span>
                                    <span class="financial-value">
                                        <v-chip
                                            :color="paymentType === 'monthly' ? 'purple lighten-4' : 'green lighten-4'"
                                            :text-color="paymentType === 'monthly' ? 'purple darken-3' : 'green darken-3'"
                                            small
                                            class="payment-chip"
                                        >
                                            <v-icon left size="14">{{ paymentType === 'monthly' ? 'fas fa-calendar-alt' : 'fas fa-hand-holding-usd' }}</v-icon>
                                            {{ paymentType === 'monthly' ? 'Monthly' : 'One-Time' }}
                                        </v-chip>
                                    </span>
                                </div>
                            </div>

                            <div class="financial-item" v-if="paymentType === 'monthly'">
                                <div class="financial-icon-wrapper blue lighten-4">
                                    <v-icon color="blue darken-2" size="20">fas fa-calendar-check</v-icon>
                                </div>
                                <div class="financial-content">
                                    <span class="financial-label">Monthly Payment</span>
                                    <span class="financial-value">{{ formatAmount(selectedAmount / 10) }} FCFA</span>
                                </div>
                            </div>

                            <div class="financial-item" v-if="paymentType === 'monthly'">
                                <div class="financial-icon-wrapper orange lighten-4">
                                    <v-icon color="orange darken-2" size="20">fas fa-list-ol</v-icon>
                                </div>
                                <div class="financial-content">
                                    <span class="financial-label">Installments</span>
                                    <span class="financial-value">10 payments</span>
                                </div>
                            </div>

                            <div class="financial-item">
                                <div class="financial-icon-wrapper green lighten-4">
                                    <v-icon color="green darken-2" size="20">fas fa-chart-line</v-icon>
                                </div>
                                <div class="financial-content">
                                    <span class="financial-label">Total Interest</span>
                                    <span class="financial-value teal--text">
                                        {{ formatAmount(getTotalInterest()) }} FCFA
                                        <span class="interest-badge">{{ getInterestPercentage() }}</span>
                                    </span>
                                </div>
                            </div>

                            <div class="financial-item">
                                <div class="financial-icon-wrapper indigo lighten-4">
                                    <v-icon color="indigo darken-2" size="20">fas fa-clock</v-icon>
                                </div>
                                <div class="financial-content">
                                    <span class="financial-label">Duration</span>
                                    <span class="financial-value">12 months</span>
                                </div>
                            </div>

                            <div class="financial-item financial-item--highlight">
                                <div class="financial-icon-wrapper success lighten-2">
                                    <v-icon color="green darken-3" size="24">fas fa-trophy</v-icon>
                                </div>
                                <div class="financial-content">
                                    <span class="financial-label">Final Amount</span>
                                    <span class="financial-value financial-value--final">
                                        {{ formatAmount(selectedAmount + getTotalInterest()) }} FCFA
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <v-divider></v-divider>

                    <div class="summary-form pa-3 pa-sm-4">
                        <div class="form-header mb-3">
                            <div class="form-header-icon">
                                <v-icon color="teal darken-2" size="22">fas fa-id-card</v-icon>
                            </div>
                            <div>
                                <div class="subtitle-1 font-weight-bold teal--text text--darken-2">Investor Verification Details</div>
                                <div class="caption grey--text">Please verify your personal information</div>
                            </div>
                        </div>

                        <v-alert
                            type="warning"
                            outline
                            dense
                            class="mb-3 caption font-weight-bold"
                            style="background-color: #fff9c4 !important; border-left: 4px solid #fbc02d !important;"
                        >
                            <v-icon color="orange darken-3" small class="mr-1">fas fa-exclamation-triangle</v-icon>
                            <strong>Rappel CNI :</strong> Le nom complet doit être <u>exactement celui inscrit sur votre carte CNI / Passeport</u> afin de garantir la conformité de vos retraits.
                        </v-alert>

                        <v-form ref="userFormValidation" v-model="formValid">
                            <v-layout row wrap>
                                <v-flex xs12 sm6 class="pa-1 pa-sm-2">
                                    <v-text-field
                                        v-model="userForm.fullName"
                                        label="Full Name (Exactly as on CNI / ID) *"
                                        hint="Must match your CNI / Passport exactly"
                                        persistent-hint
                                        outlined
                                        dense
                                        prepend-inner-icon="fas fa-id-badge"
                                        :rules="[v => !!v || 'Full Name as on CNI is required']"
                                        class="form-field"
                                    ></v-text-field>
                                </v-flex>

                                <v-flex xs12 sm6 class="pa-1 pa-sm-2">
                                    <v-text-field
                                        v-model="userForm.idCardNumber"
                                        label="ID Card / CNI Number (Optional)"
                                        hint="National ID or Passport number"
                                        persistent-hint
                                        outlined
                                        dense
                                        prepend-inner-icon="fas fa-address-card"
                                        class="form-field"
                                    ></v-text-field>
                                </v-flex>

                                <v-flex xs12 class="pa-1 pa-sm-2">
                                    <v-text-field
                                        v-model="userForm.email"
                                        label="Email Address *"
                                        hint="For confirmations & receipts"
                                        persistent-hint
                                        outlined
                                        dense
                                        prepend-inner-icon="fas fa-envelope"
                                        :rules="[
                                            v => !!v || 'Email address is required',
                                            v => /.+@.+\..+/.test(v) || 'E-mail must be valid'
                                        ]"
                                        class="form-field"
                                    ></v-text-field>
                                </v-flex>
                            </v-layout>
                        </v-form>
                    </div>
                </v-card>

                <v-layout row wrap justify-end class="mt-4 action-buttons">
                    <v-btn
                        color="grey"
                        flat
                        large
                        @click="$router.go(-1)"
                        class="mr-2 mb-2"
                    >
                        <v-icon left>fas fa-arrow-left</v-icon>
                        Cancel
                    </v-btn>
                    <v-btn
                        color="teal darken-2"
                        class="white--text"
                        large
                        :disabled="!selectedAmount || !paymentType || !formValid"
                        @click="confirmInvestment()"
                        :loading="isCreating"
                    >
                        <v-icon left>fas fa-check</v-icon>
                        Create Investment
                    </v-btn>
                </v-layout>
            </v-card-text>
        </v-card>

        <v-dialog v-model="confirmDialog" max-width="600">
            <v-card>
                <v-card-title class="headline teal darken-2 white--text">
                    <v-icon color="white" class="mr-2">fas fa-exclamation-triangle</v-icon>
                    Confirmation
                </v-card-title>
                <v-card-text class="pa-4">
                    <p class="title mb-2">Are you sure you want to create this investment?</p>
                    <div class="mb-3 grey--text text--darken-2">
                        <strong>Subscriber (CNI Name):</strong> {{ userForm.fullName }} <br>
                        <strong>Email:</strong> {{ userForm.email }} <br>
                        <span v-if="userForm.idCardNumber"><strong>ID Card No:</strong> {{ userForm.idCardNumber }}</span>
                    </div>

                    <v-alert type="warning" border="left" colored-border>
                        <strong>Important:</strong> You must make your {{ paymentType === 'monthly' ? 'first payment' : 'one-time payment' }}
                        within 2 days after creation, otherwise your investment will be automatically cancelled.
                    </v-alert>

                    <div v-if="paymentType === 'monthly'" class="mt-3">
                        <v-alert type="info" border="left" colored-border>
                            <strong>Payment Schedule:</strong>
                            <ul class="mt-2">
                                <li><strong>Month 1:</strong> {{ formatAmount(selectedAmount / 10) }} FCFA + 18% interest (FULL payment required)</li>
                                <li><strong>Months 2-10:</strong> {{ formatAmount(selectedAmount / 10) }} FCFA + decreasing interest (partial payments allowed)</li>
                                <li><strong>Interest decreases</strong> from 18% to 4.5% over 10 months</li>
                                <li>Grace period: 2 days after each due date</li>
                            </ul>
                        </v-alert>
                    </div>

                    <div v-else class="mt-3">
                        <v-alert type="info" border="left" colored-border>
                            <strong>One-Time Payment Details:</strong>
                            <ul class="mt-2">
                                <li>Pay {{ formatAmount(selectedAmount) }} FCFA once</li>
                                <li>Receive {{ formatAmount(selectedAmount + (selectedAmount * 0.18)) }} FCFA after 12 months</li>
                                <li>Fixed 18% interest on your investment</li>
                            </ul>
                        </v-alert>
                    </div>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-btn color="grey" flat @click="confirmDialog = false">Cancel</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="teal darken-2" class="white--text" @click="createInvestment()">
                        Confirm
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showManualRedirect" max-width="500" persistent>
            <v-card>
                <v-card-title class="headline orange darken-2 white--text">
                    <v-icon color="white" class="mr-2">fas fa-exclamation-circle</v-icon>
                    Action Required
                </v-card-title>
                <v-card-text class="pa-4 text-xs-center">
                    <p class="title mb-3">Your investment was created successfully!</p>
                    <p class="mb-3">Please click the button below to proceed to payment:</p>

                    <v-btn
                        color="teal darken-2"
                        class="white--text"
                        large
                        block
                        @click="forceRedirect"
                    >
                        <v-icon left>fas fa-arrow-right</v-icon>
                        Go to Payment
                    </v-btn>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
    data() {
        return {
            tabs: null,
            tabsItems: [],
            monthlyInterestRates: {
                1: 0.18,
                2: 0.165,
                3: 0.15,
                4: 0.135,
                5: 0.12,
                6: 0.105,
                7: 0.09,
                8: 0.075,
                9: 0.06,
                10: 0.045,
            },
            availableAmounts: [
                { value: 250000, label: '250,000' },
                { value: 300000, label: '300,000' },
                { value: 500000, label: '500,000' },
                { value: 700000, label: '700,000' },
                { value: 900000, label: '900,000' },
                { value: 1000000, label: '1,000,000' },
                { value: 1500000, label: '1,500,000' },
                { value: 2000000, label: '2,000,000' },
                { value: 3000000, label: '3,000,000' },
            ],
            selectedAmount: null,
            paymentType: null,
            confirmDialog: false,
            isCreating: false,
            manualRedirectUrl: '',
            showManualRedirect: false,

            formValid: false,
            userForm: {
                fullName: '',
                idCardNumber: '',
                email: ''
            }
        }
    },

    methods: {
        async getUserInfo() {
            if (!this.$store.state.isAuthenticated) return

            try {
                const response = await axios.get('/api/v1/home/get/user/info/')
                const data = response.data[0]

                this.$store.commit('setUser', data.user)
                this.$store.commit('setUserState', data.user_info)
                this.$store.commit('setUserInfo', data)

                this.populateForm(data)
            } catch (error) {
                console.error('Error loading user info:', error)
                this.initUserDataFromStore()
            }
        },

        populateForm(data) {
            if (!data || !data.user) return

            const firstName = data.user.first_name || ''
            const lastName = data.user.last_name || ''
            const full = `${firstName} ${lastName}`.trim()

            this.userForm.fullName = full || data.user.username || ''
            this.userForm.idCardNumber = data.user_profile ? data.user_profile.id_card_number : ''
            this.userForm.email = data.user.email || ''
        },

        initUserDataFromStore() {
            const userInfo = this.$store.state.user_info
            if (userInfo) {
                this.populateForm(userInfo)
            }
        },

        formatAmount(amount) {
            return new Intl.NumberFormat('fr-FR').format(amount)
        },

        getMonthlyInterestRate(month) {
            return (this.monthlyInterestRates[month] * 100).toFixed(1)
        },

        getMonthlyInterestAmount(month) {
            if (!this.selectedAmount) return 0
            const monthlyPayment = this.selectedAmount / 10
            return monthlyPayment * this.monthlyInterestRates[month]
        },

        calculateMonthlyTotalInterest() {
            if (!this.selectedAmount) return 0
            const monthlyPayment = this.selectedAmount / 10
            let totalInterest = 0

            for (let month = 1; month <= 10; month++) {
                totalInterest += monthlyPayment * this.monthlyInterestRates[month]
            }

            return totalInterest
        },

        getTotalInterest() {
            if (!this.selectedAmount) return 0

            if (this.paymentType === 'monthly') {
                return this.calculateMonthlyTotalInterest()
            } else {
                return this.selectedAmount * 0.18
            }
        },

        getInterestPercentage() {
            if (!this.selectedAmount) return ''

            const totalInterest = this.getTotalInterest()
            const percentage = (totalInterest / this.selectedAmount) * 100

            return `${percentage.toFixed(2)}%`
        },

        selectAmount(amount) {
            this.selectedAmount = amount
            this.paymentType = null

            this.$nextTick(() => {
                this.scrollToSection('paymentTypeSection')
            })
        },

        selectPaymentType(type) {
            this.paymentType = type

            this.$nextTick(() => {
                this.scrollToSection('summarySection')
            })
        },

        scrollToSection(sectionName) {
            if (this.$refs[sectionName]) {
                this.$refs[sectionName].scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
            }
        },

        confirmInvestment() {
            if (this.$refs.userFormValidation && !this.$refs.userFormValidation.validate()) {
                return
            }
            this.confirmDialog = true
        },

        async createInvestment() {
            this.isCreating = true
            this.$store.commit('setIsLoading', true)

            const formData = new FormData()
            formData.append('investment_amount', this.selectedAmount)
            formData.append('payment_type', this.paymentType)

            formData.append('full_name', this.userForm.fullName)
            formData.append('id_card_number', this.userForm.idCardNumber)
            formData.append('email', this.userForm.email)

            try {
                const response = await axios.post('/api/v1/savings/investments/create/', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                })

                if (response.data.success) {
                    this.$store.commit('setSnackBarMessage', 'Investment created successfully!')
                    this.$store.commit('setSnackBarColor', 'teal darken-2')
                    this.$store.commit('activateSnackBar', true)

                    const investmentId = response.data.investment_id
                    let targetUrl = ''

                    if (this.paymentType === 'monthly') {
                        const firstPaymentId = response.data.first_payment_id
                        targetUrl = `/savings/investments/pay/${investmentId}/${firstPaymentId}`
                    } else {
                        targetUrl = `/savings/investments/pay/${investmentId}`
                    }

                    this.performFailsafeRedirect(targetUrl)

                } else {
                    this.$store.commit('setSnackBarMessage', response.data.message || 'Error creating investment')
                    this.$store.commit('setSnackBarColor', 'red darken-4')
                    this.$store.commit('activateSnackBar', true)
                }
            } catch (error) {
                if (error.response) {
                    this.$store.commit('setSnackBarMessage', error.response.data.message || 'Server error')
                } else {
                    this.$store.commit('setSnackBarMessage', 'Connection error. Check your internet.')
                }
                this.$store.commit('setSnackBarColor', 'red darken-4')
                this.$store.commit('activateSnackBar', true)
            } finally {
                this.isCreating = false
                this.$store.commit('setIsLoading', false)
                this.confirmDialog = false
            }
        },

        performFailsafeRedirect(url) {
            let redirected = false

            try {
                this.$router.push(url).then(() => {
                    redirected = true
                }).catch(err => {
                    console.warn('Vue Router failed:', err)
                })
            } catch (e) {
                console.warn('Vue Router exception:', e)
            }

            setTimeout(() => {
                if (!redirected) {
                    try {
                        window.location.href = url
                        redirected = true
                    } catch (e) {
                        console.error('window.location.href failed:', e)
                    }
                }
            }, 500)

            setTimeout(() => {
                if (!redirected) {
                    try {
                        window.location.replace(url)
                        redirected = true
                    } catch (e) {
                        console.error('window.location.replace failed:', e)
                        this.showManualRedirectDialog(url)
                    }
                }
            }, 1500)
        },

        showManualRedirectDialog(url) {
            this.$store.commit('setSnackBarMessage', 'Automatic redirect failed. Please click the button below.')
            this.$store.commit('setSnackBarColor', 'orange darken-2')
            this.$store.commit('activateSnackBar', true)

            this.manualRedirectUrl = url
            this.showManualRedirect = true
        },

        forceRedirect() {
            window.location.href = this.manualRedirectUrl
        }
    },

    mounted() {
        document.title = "PettyCash | Create Investment"
        this.$store.commit('setCloseURL', "/savings/investments")
        this.getUserInfo()
    }
}
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════ */
/* ═══════════ INVESTMENT SUMMARY & INVESTOR DETAILS ═══════════ */
/* ═══════════════════════════════════════════════════════════════ */

/* ─── Carte principale Summary ─── */
.summary-card {
    border-radius: 16px !important;
    overflow: hidden;
    transition: all 0.3s ease;
    border: 1px solid #e0e0e0;
}

/* ─── En-tête de la section Summary ─── */
.summary-header {
    background: linear-gradient(135deg, #00897b 0%, #004d40 100%);
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 14px;
}

.summary-header-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(255,255,255,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.summary-header-text {
    flex: 1;
}

.summary-header-sub {
    opacity: 0.85;
    font-size: 13px;
}

/* ─── Grille financière ─── */
.financial-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
}

.financial-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: white;
    border-radius: 12px;
    border: 1px solid #e8e8e8;
    transition: all 0.2s ease;
}

.financial-item:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    transform: translateY(-2px);
}

.financial-item--highlight {
    background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
    border-color: #81c784;
}

.financial-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.financial-content {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.financial-label {
    font-size: 12px;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 2px;
}

.financial-value {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    word-break: break-word;
}

.financial-value--final {
    font-size: 18px;
    color: #2e7d32;
}

.interest-badge {
    display: inline-block;
    background: #e0f2f1;
    color: #00897b;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    margin-left: 6px;
}

.payment-chip {
    font-weight: 600 !important;
}

/* ─── En-tête du formulaire ─── */
.form-header {
    display: flex;
    align-items: center;
    gap: 12px;
}

.form-header-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #e0f2f1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

/* ─── Champs de formulaire ─── */
.form-field >>> .v-input__slot {
    border-radius: 10px !important;
}

.form-field >>> .v-text-field__details {
    padding-left: 4px;
}

/* ═══════════════════════════════════════════════════════════════ */
/* ═══════════════════ MOBILE RESPONSIVE ══════════════════════════ */
/* ═══════════════════════════════════════════════════════════════ */

@media (max-width: 600px) {
    /* Container */
    .investment-container {
        padding: 8px !important;
    }

    .investment-main-card {
        border-radius: 12px !important;
    }

    /* Header */
    .investment-header {
        padding: 16px 12px !important;
    }

    .header-icon-wrapper {
        margin-bottom: 8px;
    }

    .header-title {
        font-size: 20px !important;
        line-height: 1.3;
    }

    /* Terms alert */
    .terms-alert {
        padding: 12px !important;
    }

    .terms-list li {
        font-size: 13px;
        line-height: 1.5;
        margin-bottom: 4px;
    }

    /* Section titles */
    .section-title {
        font-size: 16px;
    }

    /* Amount cards */
    .amount-card {
        border-radius: 10px !important;
    }

    .amount-value {
        font-size: 16px;
    }

    .amount-label {
        font-size: 11px;
    }

    /* Payment type cards */
    .payment-type-card {
        min-height: auto !important;
        border-radius: 10px !important;
    }

    .payment-title {
        font-size: 15px;
    }

    .payment-amount {
        font-size: 22px !important;
    }

    /* ═══ SUMMARY CARD MOBILE ═══ */
    .summary-card {
        border-radius: 12px !important;
    }

    .summary-header {
        padding: 12px 14px;
        gap: 10px;
    }

    .summary-header-icon {
        width: 40px;
        height: 40px;
    }

    .summary-header-text h3 {
        font-size: 16px;
    }

    .summary-header-sub {
        font-size: 11px;
    }

    /* Financial grid mobile */
    .financial-grid {
        grid-template-columns: 1fr !important;
        gap: 8px;
    }

    .financial-item {
        padding: 10px 12px;
        gap: 10px;
    }

    .financial-icon-wrapper {
        width: 36px;
        height: 36px;
    }

    .financial-label {
        font-size: 11px;
    }

    .financial-value {
        font-size: 14px;
    }

    .financial-value--final {
        font-size: 16px;
    }

    .interest-badge {
        font-size: 10px;
        padding: 1px 6px;
    }

    /* Form header mobile */
    .form-header {
        gap: 10px;
    }

    .form-header-icon {
        width: 36px;
        height: 36px;
    }

    /* Form fields mobile */
    .form-field >>> .v-input__slot {
        min-height: 44px !important;
    }

    .form-field >>> input {
        font-size: 14px;
    }

    .form-field >>> .v-label {
        font-size: 13px;
    }

    .form-field >>> .v-messages__message {
        font-size: 11px;
    }

    /* Action buttons */
    .action-buttons {
        justify-content: center !important;
    }

    .action-buttons .v-btn {
        width: 100%;
        margin-right: 0 !important;
        margin-bottom: 8px;
    }
}

/* ═══════════════════════════════════════════════════════════════ */
/* ═══════════════════ STYLES GLOBAUX (inchangés) ════════════════ */
/* ═══════════════════════════════════════════════════════════════ */

.investment-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    transition: all 0.3s ease;
}

.investment-header {
    background: linear-gradient(135deg, #00897b 0%, #004d40 100%);
    box-shadow: 0 4px 20px rgba(0, 137, 123, 0.3);
    padding: 24px;
}

.amount-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border: 3px solid transparent;
    border-radius: 12px !important;
}

.amount-card:hover {
    transform: translateY(-5px) scale(1.02);
    border-color: #00897b;
}

.amount-card.selected {
    border-color: #00897b;
    background: linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%);
    animation: pulse 0.5s ease-in-out;
}

@keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
}

.amount-value {
    font-size: 24px;
    font-weight: bold;
    color: #00897b;
}

.amount-label {
    font-size: 14px;
    color: #666;
}

.interest-info {
    color: #004d40;
}

.payment-type-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border: 3px solid transparent;
    border-radius: 12px !important;
    min-height: 400px;
}

.payment-type-card:hover {
    transform: translateY(-5px) scale(1.02);
}

.payment-type-card.selected {
    border-color: #00897b;
    background: linear-gradient(135deg, #e0f2f1 0%, #ffffff 100%);
    animation: pulse 0.5s ease-in-out;
}

.payment-details {
    text-align: center;
}

.interest-table {
    width: 100%;
    border-collapse: collapse;
}

.interest-table td {
    padding: 4px 8px;
    border-bottom: 1px solid #e0e0e0;
}

.interest-table tr:last-child td {
    border-bottom: none;
}
</style>